import { DynamicApprenticeshipMapContent } from "@/components/apprenticeship/enterprise/map/dynamic-apprenticeship-map";
import { DynamicTodoContent } from "@/components/task/dynamic-todo-content";
import { useDynamicDetailContext } from "@/contexts/dynamic-detail";
import { cn } from "@/lib/utils";
import { DataService } from "@/services/data";
import { DynamicAction, FieldDataType, SectionDataType, Tab, TabContentResponse } from "@/types/dynamic-detail";
import { missingExpectedFields, renderField } from "@/utils/field-renderer";
import { changedFieldKeys, fieldKey } from "@/utils/changed-fields";
import { MarkdownRenderer } from "@/components/ui";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import * as LucideIcons from "lucide-react";
import { AlertCircle } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { PlaybookAnalytics } from "@/components/calls/playbook-analytics";
import { CardJournalContent, CardJournalPanel } from "@/components/dialer/cards/card-journal-panel";
import { CampaignAnalyticsContent, CampaignAnalyticsPanel } from "@/components/dialer/campaigns/analytics-panel";
import { CampaignCardsContent, CampaignCardsPanel } from "@/components/dialer/campaigns/cards-panel";
import { CampaignOperationsContent, OperationsPanel } from "@/components/dialer/campaigns/operations-panel";
import { WorkflowSlotContent, WorkflowSlotPanel } from "@/components/dialer/campaigns/workflow-slot-panel";
import { FeedingTab } from "@/components/dialer/feeding/feeding-tab";
import type { FeedingPage } from "@/components/dialer/feeding/types";
import { VersionsTab } from "@/components/dialer/versions/versions-tab";
import { WorkflowVersionsPanel, WorkflowVersionsPanelContent } from "@/components/dialer/versions/workflow-versions-panel";
import { WorkflowLineage } from "@/types/dialer/workflow";
import { CallAnalyticsType } from "@/types/voice/call_analytics";
import { Actions2 } from "./actions-2";
import { AudioTranscription2 } from "./audio-transcription-2";
import { eventLogItemsFromPayload } from "@/utils/event-log-payload";
import { Section, StateBlock } from "./ds";
import { EventLog } from "./event-log";
import { DynamicRelatedListContent } from "./dynamic-related-list-content";
import { FieldLabelWithDescription } from "./field-label-with-description";
import { FieldsHistory } from "./fields-history";
import { InlineEditField } from "./inline-edit-field";
import { LocationMap } from "./location-map";
import { InteractionsHistory } from "./interactions-history";

// Composant pour afficher une seule section
export function DynamicSection({
    section,
    objectType,
    objectId,
    canUpdate,
    onFieldUpdate,
    recordActions,
    changed
}: {
    section: SectionDataType;
    objectType?: string;
    objectId?: string | number;
    canUpdate?: boolean;
    onFieldUpdate?: () => void;
    /** The record's actions (`detail-data`): those the section names are offered in its header. */
    recordActions?: DynamicAction[];
    /** Fields whose value just changed (`changedFieldKeys`): lit up for a moment, then faded. */
    changed?: Set<string>;
}) {
    const [isOpen, setIsOpen] = useState(true);

    const renderFieldInSection = (field: FieldDataType) => {
        // A value that just changed is lit, then fades back (the transition runs both ways).
        const lit = cn("rounded-sm transition-colors duration-1000", changed?.has(fieldKey(section.name, field.field_name)) && "bg-amber-100");
        // If field is editable + user has update permission + we have the context
        if (field.editable && canUpdate && objectType && objectId) {
            return (
                <div
                    className={cn("grid items-start border-b pb-1", field.large_field ? "col-span-8 grid-cols-8" : "col-span-8 grid-cols-8 sm:col-span-4 sm:grid-cols-4", lit)}
                    data-changed={changed?.has(fieldKey(section.name, field.field_name)) || undefined}
                >
                    <FieldLabelWithDescription
                        label={field.label}
                        description={field.description}
                        descriptionTrigger="label"
                        className="col-span-2 mt-0.5 min-w-0 truncate text-ellipsis pr-1 text-xs text-gray-600 sm:col-span-1"
                    />
                    <div className={cn("flex min-w-0 items-center", field.large_field ? "col-span-6 sm:col-span-7" : "col-span-6 sm:col-span-3")}>
                        <InlineEditField field={field} objectType={objectType} objectId={objectId} onSuccess={onFieldUpdate} siblingFields={section.fields ?? []} />
                    </div>
                </div>
            );
        }

        // Standard read-only rendering
        return renderField(field, {
            layout: "grid",
            showLabel: true,
            labelClassName: "col-span-2 mt-0.5 min-w-0 truncate text-ellipsis pr-1 text-xs text-gray-600 sm:col-span-1",
            valueClassName: "text-sm",
            containerClassName: cn("grid items-start border-b pb-1", field.large_field ? "col-span-8 grid-cols-8" : "col-span-8 grid-cols-8  sm:col-span-4 sm:grid-cols-4", lit),
            largeField: field.large_field,
            showUnit: true,
            isHeaderField: false,
            descriptionTrigger: "label"
        });
    };

    const getIcon = (iconName: string) => {
        const Icon = (LucideIcons as any)[iconName];
        return Icon ? <Icon className="h-4 w-4" /> : null;
    };

    const icon = getIcon(section.icon);
    const missing = missingExpectedFields(section.fields);
    // Only the actions the record serves to this user: the server already decided who may run them.
    const offered = (section.actions ?? []).flatMap(item => recordActions?.find(action => action.action_name === item.action_name) ?? []);
    const labels = Object.fromEntries((section.actions ?? []).filter(item => item.label).map(item => [item.action_name, item.label as string]));
    const headerActions =
        offered.length > 0 && objectType && objectId != null ? (
            <Actions2
                actions={offered}
                labels={labels}
                layout="inline"
                selectedItemIds={[String(objectId)]}
                selectAll={false}
                selectAllCount={0}
                endpointUrl={`/crm/objects/${objectType}/`}
                action_scope="detail"
                onActionSuccess={onFieldUpdate}
            />
        ) : null;

    return (
        <div className="mt-4 w-full overflow-hidden">
            {section.display_separator && (
                <div className="flex w-full items-center gap-2 rounded bg-gray-100 pr-1">
                    <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center justify-between gap-2 px-2 py-1.5 font-medium text-gray-700"
                        onClick={() => section.collapsible && setIsOpen(!isOpen)}
                        aria-expanded={isOpen}
                    >
                        <div className="flex items-center gap-2">
                            {icon && <div className="inline-block">{icon}</div>}
                            <span className="text-sm font-normal text-black">{section.name}</span>
                            {missing.length > 0 && (
                                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800" title={missing.map(field => field.label).join(", ")}>
                                    {missing.length} à compléter
                                </span>
                            )}
                        </div>
                        {section.collapsible && <ChevronDownIcon className={cn("h-4 w-4 transition-transform duration-200", isOpen ? "rotate-180" : "")} />}
                    </button>
                    {headerActions}
                </div>
            )}
            <div className={cn("overflow-hidden transition-all duration-200 ease-in-out", section.collapsible ? (isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0") : "opacity-100")}>
                {section.markdown_content ? (
                    <div className="bg-white p-2">
                        <MarkdownRenderer content={section.markdown_content} />
                    </div>
                ) : (
                    <div className={cn("grid grid-cols-8 gap-2 bg-white p-2")}>
                        {(section.fields || []).map((field, i) => (
                            <React.Fragment key={`${field.field_name}-${i}`}>{renderFieldInSection(field)}</React.Fragment>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

interface DynamicTabContentProps {
    objectType: string;
    objectId: string | number;
    tab: Tab;
    /** Path under the tab (`runs/12`): what a tab that has drill-down pages is showing. */
    subPath?: string;
}

export function DynamicTabContent({ objectType, objectId, tab, subPath }: DynamicTabContentProps) {
    const [content, setContent] = useState<TabContentResponse | null>(null);
    // The tab (and record) the content shown belongs to: another one is loaded with a spinner, the same one
    // again (after an action) keeps its content on screen while it is fetched.
    const contentKey = `${objectType}/${objectId}/${tab.route_path}`;
    const [shownKey, setShownKey] = useState<string | null>(null);
    const [changed, setChanged] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const contentRef = useRef<TabContentResponse | null>(null);
    const shownKeyRef = useRef<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [refetchKey, setRefetchKey] = useState(0);

    const { refetch: refetchDetail, metadata, detailData } = useDynamicDetailContext();
    // The record fetched again (an action of the header, a section's, an inline edit): its tab follows.
    const detailVersion = useRef(0);
    const [detailRefresh, setDetailRefresh] = useState(0);
    useEffect(() => {
        if (detailVersion.current++ > 0) setDetailRefresh(version => version + 1);
    }, [detailData]);

    const canUpdate = metadata.permissions?.update !== "none";

    const handleMutate = useCallback(() => setRefetchKey(k => k + 1), []);

    // The tab follows the record's refetch (`detailRefresh`).
    const handleFieldUpdate = useCallback(() => {
        refetchDetail();
    }, [refetchDetail]);

    useEffect(() => {
        const fetchTabContent = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = (await DataService.client.url(`/crm/objects/${objectType}/${objectId}/tab-content/${tab.route_path}/`).get().json()) as TabContentResponse;

                const sameTab = shownKeyRef.current === contentKey;
                setChanged(sameTab ? changedFieldKeys(contentRef.current?.sections, response.sections) : new Set());
                contentRef.current = response;
                shownKeyRef.current = contentKey;
                setContent(response);
                setShownKey(contentKey);
            } catch (err) {
                console.error("Error fetching tab content:", err);
                setError("Error loading the content");
            } finally {
                setLoading(false);
            }
        };

        if (tab.name && objectId) {
            fetchTabContent();
        }
    }, [objectType, objectId, tab.name, tab.route_path, refetchKey, detailRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

    // The lit values fade after a moment.
    useEffect(() => {
        if (changed.size === 0) return;
        const timer = setTimeout(() => setChanged(new Set()), 1500);
        return () => clearTimeout(timer);
    }, [changed]);

    if (loading && shownKey !== contentKey) {
        return (
            <div className="mt-4 flex items-start justify-start">
                <LucideIcons.Loader className="h-5 w-5 animate-spin text-gray-500" />
                <span className="ml-2 text-sm text-gray-500">Loading...</span>
            </div>
        );
    }

    if (error || content?.error) {
        return (
            <div className="mt-4 flex items-center justify-center">
                <div className="flex items-center gap-2 text-sm text-red-500">
                    <AlertCircle className="h-4 w-4" />
                    {error || content?.error}
                </div>
            </div>
        );
    }

    if (!content) {
        return (
            <div className="mt-4 flex items-center justify-center">
                <div className="text-sm text-gray-500">No content available</div>
            </div>
        );
    }

    // Afficher le contenu en fonction du type
    switch (content.content_type) {
        case "sections":
            if (content.sections && content.sections.length > 0) {
                return (
                    <div className="space-y-6">
                        {content.sections
                            .filter(section => (section.fields && section.fields.length > 0) || !!section.markdown_content)
                            .map((section, index) => (
                                <DynamicSection
                                    key={`${section.name}-${index}`}
                                    section={section}
                                    objectType={objectType}
                                    objectId={objectId}
                                    canUpdate={canUpdate}
                                    onFieldUpdate={handleFieldUpdate}
                                    recordActions={detailData?.actions}
                                    changed={changed}
                                />
                            ))}
                    </div>
                );
            }
            return <div className="mt-4 text-sm text-gray-500">No sections configured for this tab</div>;

        case "history":
            if (content.history && content.history.length > 0) {
                return <FieldsHistory history={content.history} />;
            }

            return <div className="mt-4 text-sm text-gray-500">No history available</div>;

        case "todo":
            if (content.todo) {
                return <DynamicTodoContent objectType={objectType} objectId={typeof objectId === "string" ? parseInt(objectId) : objectId} />;
            }

            return <div className="mt-4 text-sm text-gray-500">To do feature is not yet available</div>;

        case "interactions":
            if (content.interactions) {
                return (
                    <InteractionsHistory
                        interactions={content.interactions}
                        count={content.count}
                        next={content.next}
                        filters={content.filters}
                        canViewAccountInteractions={content.can_view_account_interactions}
                        className="mt-4"
                        noteForm={content.note_form}
                        objectType={objectType}
                        objectId={objectId}
                    />
                );
            }
            return <div className="mt-4 text-sm text-gray-500">No interactions available</div>;

        case "call-transcription":
            if (content.transcription) {
                return <AudioTranscription2 transcription={content.transcription} />;
            }
            return <div className="mt-4 text-sm text-gray-500">No transcription available</div>;

        case "call-analytics":
            if (content.analytics) {
                return <PlaybookAnalytics analytics={content.analytics as CallAnalyticsType} />;
            }
            return <div className="mt-4 text-sm text-gray-500">No playbook analysis available for this call</div>;

        case "related-list":
            return <DynamicRelatedListContent content={content} objectId={objectId} onMutate={handleMutate} />;

        case "apprenticeship-map":
            if (content.map) {
                return <DynamicApprenticeshipMapContent objectId={objectId} map={content.map} />;
            }
            return <div className="mt-4 text-sm text-gray-500">Coordonnées GPS non disponibles</div>;

        case "location-map": {
            const { latitude, longitude, address, company_name, candidates, candidate_filters, candidate_radius_km } = content.location_map ?? {};
            // The pin is the tab's reason to exist: without a pair of numbers the backend
            // resolved nothing, and `LocationMap` would center on the ocean.
            if (!latitude || !longitude || isNaN(parseFloat(latitude)) || isNaN(parseFloat(longitude))) {
                return <div className="mt-4 text-sm text-gray-500">Coordonnées GPS non disponibles</div>;
            }
            return (
                <LocationMap
                    latitude={parseFloat(latitude)}
                    longitude={parseFloat(longitude)}
                    address={address}
                    company_name={company_name}
                    candidates={candidates}
                    candidateFilters={candidate_filters}
                    candidateRadiusKm={candidate_radius_km}
                />
            );
        }

        case "dialer-workflow-slot":
            if (content.workflow_slot) {
                return <WorkflowSlotPanel content={content.workflow_slot as WorkflowSlotContent} onMutate={handleMutate} />;
            }
            return <StateBlock message="No workflow slot available for this campaign" />;

        case "dialer-campaign-operations":
            if (content.operations) {
                // `handleFieldUpdate`: activate / pause moves the status quick field on
                // the record header, and a rollback moves the slot labels beside it.
                return <OperationsPanel content={content.operations as CampaignOperationsContent} onMutate={handleFieldUpdate} />;
            }
            return <StateBlock message="No operations available for this campaign" />;

        case "dialer-campaign-cards":
            if (content.cards) {
                return <CampaignCardsPanel campaignId={objectId} initial={content.cards as CampaignCardsContent} />;
            }
            return <StateBlock message="No cards available for this campaign" />;

        case "dialer-campaign-analytics":
            if (content.analytics) {
                return <CampaignAnalyticsPanel campaignId={objectId} initial={content.analytics as CampaignAnalyticsContent} />;
            }
            return <StateBlock message="No analytics available for this campaign" />;

        case "dialer-card-journal":
            if (content.journal) {
                return <CardJournalPanel content={content.journal as CardJournalContent} />;
            }
            return <StateBlock message="No journal available for this card" />;

        case "event-log": {
            const items = eventLogItemsFromPayload(content.events);
            return (
                <Section icon="History" title={content.title ? `${content.title} (${items.length})` : `Journal (${items.length})`}>
                    {content.description && <p className="mb-3 text-xs text-gray-500">{content.description}</p>}
                    <EventLog items={items} emptyMessage={content.empty_message ?? undefined} />
                </Section>
            );
        }

        case "settings-page":
            if (content.page) {
                // `handleFieldUpdate`: putting a campaign in service or pausing it moves the
                // status quick field on the record header.
                return <FeedingTab page={content.page as FeedingPage} subPath={subPath} onServiceChange={handleFieldUpdate} />;
            }
            return <StateBlock message="No settings available for this tab" />;

        case "workflow-versions":
            if (content.lineage) {
                // `handleFieldUpdate` rather than `handleMutate`: deploying or discarding
                // moves the version count and the deployment badges the record's own
                // header and sections state, so both have to be read again.
                return <VersionsTab lineage={content.lineage as WorkflowLineage} onMutate={handleFieldUpdate} />;
            }
            return <div className="mt-4 text-sm text-gray-500">No version available for this workflow</div>;

        case "workflow-versions-panel":
            if (content.version_summary) {
                return <WorkflowVersionsPanel content={content.version_summary as WorkflowVersionsPanelContent} />;
            }
            return <div className="mt-4 text-sm text-gray-500">No version summary available for this workflow</div>;

        case "custom":
            return <div className="mt-4 text-sm text-gray-500">{content.message || "Custom content to implement"}</div>;

        default:
            return <div className="mt-4 text-sm text-red-500"> Unsupported content type: {content.content_type}</div>;
    }
}
